/**
 * Box-per-limb humanoid silhouette. One shared widget for the sidebar
 * (player + focused target) and the accuracy panel. Each limb's box is
 * colored by that limb's current HP band.
 *
 * Layout is a stack of absolute-positioned <div>s inside a fixed-aspect
 * container so it scales nicely regardless of the panel width.
 */

import type { BodyPart, BodyState } from '../sim/body';

/** Fraction-of-panel bounds for each limb box. */
interface LimbLayout {
  part: BodyPart;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Silhouette geometry — head at top, torso center, arms flanking, legs
 *  bottom. Matches the body-panel space used by the reticule but scaled
 *  into 0..1 fraction of the widget's bounding box. */
export const LIMB_LAYOUT: readonly LimbLayout[] = [
  { part: 'head', left: 0.40, top: 0.05, width: 0.20, height: 0.15 },
  { part: 'leftArm', left: 0.10, top: 0.24, width: 0.18, height: 0.36 },
  { part: 'rightArm', left: 0.72, top: 0.24, width: 0.18, height: 0.36 },
  { part: 'torso', left: 0.30, top: 0.22, width: 0.40, height: 0.40 },
  { part: 'leftLeg', left: 0.30, top: 0.64, width: 0.18, height: 0.32 },
  { part: 'rightLeg', left: 0.52, top: 0.64, width: 0.18, height: 0.32 },
];

/** Health-band → box background color. Grey = destroyed. */
function limbColor(pct: number, crippled: boolean, dead: boolean): string {
  if (dead) return '#3a2020';
  if (crippled) return '#5a2727';
  if (pct >= 0.85) return '#4bd25a';
  if (pct >= 0.55) return '#c4c045';
  if (pct >= 0.25) return '#e08830';
  if (pct > 0) return '#e04040';
  return '#2a2a2a';
}

export interface ReticuleOverlay {
  /** Body-panel-space X (~ -0.5..0.5) — center of the reticule. */
  x: number;
  /** Body-panel-space Y (~ -0.9..0.9). */
  y: number;
}

/**
 * Build a self-contained HTMLElement rendering the limb silhouette.
 * Optional reticule overlay: a small `+` glyph positioned in body-panel
 * space (mapped to the same axes as LIMB_LAYOUT so it visually lands on
 * the limb the sim would resolve as the hit).
 */
export function renderLimbSilhouette(
  body: BodyState,
  label: string,
  reticule?: ReticuleOverlay,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'limb-silhouette';
  box.innerHTML = `<div class="ls-label">${label}${body.dead ? ' · DEAD' : ''}</div><div class="ls-frame"></div>`;
  const frame = box.querySelector('.ls-frame') as HTMLElement;
  for (const l of LIMB_LAYOUT) {
    const hp = body.hp[l.part];
    const max = body.hpMax[l.part];
    const pct = max > 0 ? hp / max : 0;
    const crip = body.crippled[l.part];
    const el = document.createElement('div');
    el.className = 'ls-limb';
    el.style.left = `${l.left * 100}%`;
    el.style.top = `${l.top * 100}%`;
    el.style.width = `${l.width * 100}%`;
    el.style.height = `${l.height * 100}%`;
    el.style.background = limbColor(pct, crip, body.dead);
    el.title = `${l.part}: ${hp.toFixed(0)}/${max}${crip ? ' (crippled)' : ''}`;
    frame.appendChild(el);
  }
  if (reticule) {
    // Map body-panel space to panel-fraction. Body x span 1.0 → panel x
    // span 1.0 (edges of silhouette hug edges of panel). Body y span ~1.8
    // compresses onto the panel's 1.0 vertical span so the reticule can't
    // wander off the top/bottom.
    const px = Math.max(0, Math.min(1, 0.5 + reticule.x));
    const py = Math.max(0, Math.min(1, 0.5 + reticule.y * 0.55));
    const ret = document.createElement('div');
    ret.className = 'ls-reticule';
    ret.textContent = '+';
    ret.style.left = `${px * 100}%`;
    ret.style.top = `${py * 100}%`;
    frame.appendChild(ret);
  }
  return box;
}
